import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { KioskCreated, KioskView, MobileDeviceView } from '../types'

export function useMyDevices() {
  return useQuery({
    queryKey: ['me', 'devices'],
    queryFn: async () => (await apiClient.get<MobileDeviceView[]>('/v1/me/devices')).data,
  })
}

export function useDeviceMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['me', 'devices'] })

  const revoke = useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
      (await apiClient.delete<MobileDeviceView>(`/v1/me/devices/${id}`, {
        data: { reason: reason ?? null },
      })).data,
    onSuccess: invalidate,
  })

  return { revoke }
}

export function useKiosks() {
  return useQuery({
    queryKey: ['kiosks'],
    queryFn: async () => (await apiClient.get<KioskView[]>('/v1/kiosks')).data,
  })
}

export function useKioskMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['kiosks'] })

  const create = useMutation({
    mutationFn: async (body: { name: string; locationId: string; expiresAt: string }) =>
      (await apiClient.post<KioskCreated>('/v1/kiosks', body)).data,
    onSuccess: invalidate,
  })

  const revoke = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.delete<KioskView>(`/v1/kiosks/${id}`)).data,
    onSuccess: invalidate,
  })

  return { create, revoke }
}
